import type { RidershipReadinessIssue } from '../../../types/ridershipReadinessTypes';
import { FISCAL_MONTH_NAMES } from './reportingScope';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function fiscalMonthToUtcMs(monthName: string, fiscalYear: number): number | null {
    const index = FISCAL_MONTH_NAMES.findIndex(
        name => name.localeCompare(monthName, undefined, { sensitivity: 'accent' }) === 0,
    );
    if (index < 0) {
        return null;
    }

    const calendarYear = index <= 2 ? fiscalYear - 1 : fiscalYear;
    const calendarMonth = index <= 2 ? 10 + index : index - 2;
    return Date.UTC(calendarYear, calendarMonth - 1, 1);
}

function extractFiscalMonthName(issue: RidershipReadinessIssue): string | null {
    const evidence = issue.evidence?.trim();
    if (evidence && !ISO_DATE.test(evidence)) {
        const monthToken = evidence.split('/')[0]?.trim();
        if (monthToken && fiscalMonthToUtcMs(monthToken, 2000) != null) {
            return monthToken;
        }
    }

    for (const monthName of FISCAL_MONTH_NAMES) {
        if (issue.plainLanguage.includes(monthName)) {
            return monthName;
        }
    }

    return null;
}

/** Sort key by event time (ms). Issues without a parseable date sort last when newest-first. */
export function getIssueSortTime(issue: RidershipReadinessIssue, fiscalYear: number): number {
    const evidence = issue.evidence?.trim();
    if (evidence && ISO_DATE.test(evidence)) {
        return Date.parse(`${evidence}T00:00:00Z`);
    }

    const monthName = extractFiscalMonthName(issue);
    if (monthName) {
        const monthStart = fiscalMonthToUtcMs(monthName, fiscalYear);
        if (monthStart != null) {
            return monthStart;
        }
    }

    return Number.POSITIVE_INFINITY;
}

export function sortReadinessIssuesNewestFirst(
    issues: RidershipReadinessIssue[],
    fiscalYear: number,
): RidershipReadinessIssue[] {
    return [...issues].sort((left, right) => {
        const timeDelta = getIssueSortTime(right, fiscalYear) - getIssueSortTime(left, fiscalYear);
        if (timeDelta !== 0) {
            return timeDelta;
        }

        return left.plainLanguage.localeCompare(right.plainLanguage);
    });
}
